import React from 'react';

/**
 * Shared SVG rendering utilities — used by CanvasEditor, PageThumbnailPreview,
 * and any future renderer so gradient output is always consistent.
 */

/**
 * Build a <linearGradient> or <radialGradient> JSX element.
 *
 * @param {string} gradId   - Unique SVG element id
 * @param {object} gradient - { type, colors[], positions[], opacities[], angle }
 *                            positions[] is 0-100 (percent scale)
 *                            angle is degrees, 0 = top-to-bottom, 90 = left-to-right
 * @returns {JSX.Element|null}
 */
export const buildSVGGradient = (gradId, gradient) => {
  if (!gradient || !gradient.colors || gradient.colors.length < 2) return null;

  const {
    type      = 'linear',
    colors,
    positions = [],
    opacities = [],
    angle     = 90,
  } = gradient;

  const stops = colors.map((c, i) => (
    <stop
      key={i}
      offset={`${positions[i] ?? (i === 0 ? 0 : 100)}%`}
      stopColor={c}
      stopOpacity={opacities[i] ?? 1}
    />
  ));

  if (type === 'radial' || type === 'diamond') {
    return (
      <radialGradient id={gradId} cx="50%" cy="50%" r="50%" gradientUnits="objectBoundingBox">
        {stops}
      </radialGradient>
    );
  }

  // linear / angular — both map to SVG linearGradient
  // Convert CSS-style angle (0° = top, clockwise) to SVG x1/y1/x2/y2
  const rad = (angle * Math.PI) / 180;
  const x1  = (0.5 - 0.5 * Math.sin(rad)).toFixed(4);
  const y1  = (0.5 + 0.5 * Math.cos(rad)).toFixed(4);
  const x2  = (0.5 + 0.5 * Math.sin(rad)).toFixed(4);
  const y2  = (0.5 - 0.5 * Math.cos(rad)).toFixed(4);

  return (
    <linearGradient id={gradId} x1={x1} y1={y1} x2={x2} y2={y2} gradientUnits="objectBoundingBox">
      {stops}
    </linearGradient>
  );
};

/**
 * Convert a hex colour + alpha to an rgba() string.
 * Returns the original hex unchanged if alpha >= 1 or if hex is invalid.
 *
 * @param {string} hex    - e.g. "#ff0000"
 * @param {number} alpha  - 0–1
 * @returns {string}
 */
export const hexToRgba = (hex, alpha = 1) => {
  const m = (hex || '').match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return hex || 'transparent';
  if (alpha >= 1) return hex;
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${alpha})`;
};

/**
 * Build a CSS linear-gradient() string from a fillGradient / backgroundConfig.gradient object.
 * Used for CSS backgrounds (not SVG fill).
 *
 * @param {object} fg - same gradient structure as buildSVGGradient
 * @returns {string}  - CSS gradient string
 */
export const buildGradientCss = (fg) => {
  if (!fg || !fg.colors) return 'transparent';
  const stops = (fg.colors || []).map((c, i) => {
    const pos = fg.positions?.[i] ?? (i / Math.max(1, fg.colors.length - 1)) * 100;
    const op  = fg.opacities?.[i] ?? 1;
    return `${hexToRgba(c, op)} ${pos}%`;
  }).join(', ');
  return `linear-gradient(${fg.angle ?? 90}deg, ${stops})`;
};

/**
 * Normalise element opacity fields so renderers always have concrete numeric values.
 * Call this before rendering any element to guarantee no undefined opacity checks.
 *
 * @param {object} el - raw element from page.elements[]
 * @returns {object}  - element with opacity, fillOpacity, strokeOpacity guaranteed
 */
export const normalizeElement = (el) => {
  if (!el) return el;
  return {
    ...el,
    opacity:       el.opacity       ?? 1,
    fillOpacity:   el.fillOpacity   ?? 1,
    strokeOpacity: el.strokeOpacity ?? 1,
  };
};
